import {
	Button,
	DateTimePicker,
	HStack,
	Select,
	SelectOptionType,
} from '@montel/montelpro-shared-components-v2';
import {
	PROVIDER_ACCESS_EXPORT_COMPANY_LEVEL_ACCESS,
	PROVIDER_ACCESS_GET_COMPANY_LEVEL_ACCESS,
	SUBSCRIPTIONS_GET_PROVIDERS,
} from 'apiRoutes';
import { Provider } from 'domain/subscription/interfaces/Subscription';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { FC, useEffect, useState } from 'react';
import { RequestStatus } from 'types/RequestStatus';
import { useCompanyLevelFormContext } from './CompanyLevelFormContext';
import { useGet } from 'hooks/useGet';
import { CompanyRealtimeAccess } from '../types';
import saveAs from 'file-saver';
import { toDefaultDateFormatOrNotAvailable } from 'utils/dateUtils';
import SwitchInput from 'components/common/SwitchInput';

const CompanyLevelForm: FC = () => {
	const {
		formState,
		setFormState,
		setProviderAccessLogs,
		setAccessEntriesStatus,
	} = useCompanyLevelFormContext();
	const applicationId = process.env.REACT_APP_MARKETS_APPLICATION_ID || '';

	useEffect(() => {
		setFormState((prevState) => ({
			...prevState,
			applicationId: applicationId,
		}));
	}, []);

	const { data: providers, status: providersStatus } = useGetOnMount<
		Provider[]
	>(`${SUBSCRIPTIONS_GET_PROVIDERS}?applicationId=${applicationId}`);

	const {
		data: companyLevelAccessEntries,
		getData: getCompanyLevelAccessEntries,
		status: getCompanyLevelAccessEntriesStatus,
	} = useGet<CompanyRealtimeAccess[]>(
		`${PROVIDER_ACCESS_GET_COMPANY_LEVEL_ACCESS}?applicationId=${applicationId}&providerCode=${
			formState.providerCode
		}&startDate=${formState.start.toISOString()}&endDate=${formState.end.toISOString()}&realtimeOnly=${
			formState.realtimeOnly
		}`,
	);

	const {
		getData: getCompanyLevelAccessExport,
		status: getCompanyLevelAccessExportStatus,
	} = useGet<Blob>(
		`${PROVIDER_ACCESS_EXPORT_COMPANY_LEVEL_ACCESS}?applicationId=${applicationId}&providerCode=${
			formState.providerCode
		}&startDate=${formState.start.toISOString()}&endDate=${formState.end.toISOString()}&realtimeOnly=${
			formState.realtimeOnly
		}`,
		{ responseType: 'blob' },
	);

	const [providerOptions, setProviderOptions] =
		useState<SelectOptionType[]>();

	const [selectedProviderOption, setSelectedProviderOption] =
		useState<SelectOptionType | null>(null);

	useEffect(() => {
		if (!!providers) {
			const providerOptions = providers.map((provider) => ({
				value: provider.code,
				label: provider.name,
			}));
			setProviderOptions(providerOptions);
		}
	}, [providers]);

	useEffect(() => {
		if (companyLevelAccessEntries) {
			setProviderAccessLogs(companyLevelAccessEntries);
		}
	}, [companyLevelAccessEntries]);

	useEffect(() => {
		setAccessEntriesStatus(getCompanyLevelAccessEntriesStatus);
	}, [getCompanyLevelAccessEntriesStatus]);

	return (
		<HStack fullWidth justify="space-between" align="flex-end">
			<HStack wrap="nowrap" align="flex-end" gap="md">
				<Select
					label={'Provider'}
					required
					options={providerOptions || []}
					disabled={providersStatus !== RequestStatus.SUCCESS}
					placeholder="Select provider"
					value={selectedProviderOption}
					onChange={(option) => {
						if (!option) return;

						setSelectedProviderOption(option);
						setFormState((prevState) => ({
							...prevState,
							providerCode: option?.value || '',
						}));
					}}
				/>
				<DateTimePicker
					label={'Start date'}
					withTime={false}
					value={formState.start}
					onChange={(date) => {
						setFormState((prevState) => ({
							...prevState,
							start: date || new Date(),
						}));
					}}
				/>
				<DateTimePicker
					label={'End date'}
					withTime={false}
					value={formState.end}
					onChange={(date) => {
						setFormState((prevState) => ({
							...prevState,
							end: date || new Date(),
						}));
					}}
				/>
				<SwitchInput
					label="Realtime only"
					isChecked={formState.realtimeOnly}
					onChange={(checked) => {
						setFormState((prevState) => ({
							...prevState,
							realtimeOnly: checked,
						}));
					}}
				/>
			</HStack>
			<HStack wrap="nowrap" align="flex-end">
				<Button
					text={
						getCompanyLevelAccessEntriesStatus ===
						RequestStatus.UNLOADED
							? 'Load'
							: 'Refresh'
					}
					isLoading={
						getCompanyLevelAccessEntriesStatus ===
						RequestStatus.LOADING
					}
					isDisabled={
						!formState.providerCode ||
						!formState.start ||
						!formState.end
					}
					onClick={async () => {
						await getCompanyLevelAccessEntries();
					}}
				/>
				<Button
					text={'Export'}
					isLoading={
						getCompanyLevelAccessExportStatus ===
						RequestStatus.LOADING
					}
					isDisabled={
						!formState.providerCode ||
						!formState.start ||
						!formState.end
					}
					onClick={async () => {
						const blob = await getCompanyLevelAccessExport();
						if (blob) {
							const startDate = toDefaultDateFormatOrNotAvailable(
								formState.start,
								'',
								'DDMMYY',
							);
							const endDate = toDefaultDateFormatOrNotAvailable(
								formState.end,
								'',
								'DDMMYY',
							);
							saveAs(
								blob,
								`company-level-access_${formState.providerCode}-${startDate}-${endDate}.xlsx`,
							);
						}
					}}
				/>
			</HStack>
		</HStack>
	);
};

export default CompanyLevelForm;
